/**
 * Die Deckung des Zahlungsplans in EINEM Satz — fuer alle Stellen, die sie anzeigen.
 *
 * WARUM diese Datei existiert (Z0.3/Z0.4, `docs/KONZEPT-ZAHLUNGSPLAN.md` Befund B-3): Die
 * Kennzahl selbst wird serverseitig an genau EINER Stelle gerechnet
 * (`apps/api/src/lib/zahlungsdeckung.ts`) und liegt fertig in der Antwort. Was die Oberflaeche
 * noch zu tun hat, ist Formulieren — und auch das gehoert an eine Stelle: der Vorgang und die
 * Zahlungs-Uebersicht muessen denselben Satz zeigen. Zwei Formulierungen derselben Zahl lesen
 * sich wie zwei Aussagen, und beim Geld ist die widerspruechliche Anzeige teurer als die
 * fehlende.
 *
 * WAS hier NICHT passiert: Es wird nichts gerechnet. Kein Betrag wird verglichen, keine Differenz
 * gebildet, keine Richtung abgeleitet. `richtung` und `differenzCents` kommen vom Server; diese
 * Datei setzt sie in Worte. Die Ratsche `apps/api/test/deckung-eine-rechenstelle.test.ts` wird
 * rot, sobald hier (oder anderswo im Web-Quellbaum) doch gerechnet wird.
 */
import { money } from "../ui/format";

/** Spiegel von `Deckungsrichtung` in `apps/api/src/lib/zahlungsdeckung.ts`. */
export type Deckungsrichtung = "gedeckt" | "unterdeckt" | "ueberdeckt" | "unbekannt";

/** Spiegel von `Deckung` in `apps/api/src/lib/zahlungsdeckung.ts` — so kommt sie in der Antwort an. */
export interface Deckung {
  planSummeCents: number;
  /** Zahl der Zeilen, die zum Plan zaehlen. 0 = es gibt noch gar keinen Plan. */
  zeilenImPlan: number;
  angebotssummeCents: number | null;
  /** Immer >= 0 — das Vorzeichen traegt `richtung`, nicht der Betrag. */
  differenzCents: number;
  richtung: Deckungsrichtung;
}

/**
 * Der Hinweissatz, oder `null`, wenn es nichts zu melden gibt.
 *
 * `null` bei `gedeckt` (ein stimmiger Plan braucht keinen Hinweis — sonst steht auf jeder
 * Vorgangsseite eine Zeile, die nie etwas sagt, und niemand liest sie mehr), bei `unbekannt`
 * (ohne Angebotssumme waere jede Aussage geraten), wenn gar keine Kennzahl vorliegt
 * (aeltere Antwort, Ladezustand) — und **wenn es noch keinen Plan gibt**.
 *
 * Der letzte Fall ist Antoines Entscheidung vom 12.08.2026, im Wortlaut: *„Nur bei echtem Plan
 * zeigen."* Anlass war ein frisch angelegter Vorgang auf `storia.schrittmacher.ai`: unter „Noch
 * keine Zahlungen erfasst." stand „Der Zahlungsplan fordert 0,00 € von 2.625,00 € Angebotssumme
 * — es fehlen 2.625,00 €." Beides sagt dasselbe, und die zweite Fassung klingt nach einem Fehler,
 * obwohl an einem Vorgang ohne Zahlungen nichts falsch ist. Ab der ersten geplanten Zahlung
 * erscheint der Satz unveraendert.
 *
 * Die Rechnung bleibt davon unberuehrt: ein Vorgang ohne Zeilen ist weiterhin `unterdeckt`, und
 * `differenzCents` nennt weiterhin die volle Angebotssumme. Geaendert ist nur, ob daraus ein Satz
 * wird. `!deckung.zeilenImPlan` faengt dabei bewusst auch `undefined` mit (aeltere Antwort ohne
 * das Feld): im Zweifel schweigen ist hier die richtige Vorgabe — der fehlende Hinweis kostet
 * einen Blick, der falsche das Vertrauen in alle uebrigen.
 */
export function deckungsSatz(deckung: Deckung | null | undefined): string | null {
  if (!deckung) return null;
  if (!deckung.zeilenImPlan) return null;
  const plan = money(deckung.planSummeCents);
  const angebot = money(deckung.angebotssummeCents);
  const differenz = money(deckung.differenzCents);
  switch (deckung.richtung) {
    case "unterdeckt":
      return `Der Zahlungsplan fordert ${plan} von ${angebot} Angebotssumme — es fehlen ${differenz}.`;
    case "ueberdeckt":
      return `Der Zahlungsplan fordert ${plan} bei ${angebot} Angebotssumme — ${differenz} zu viel.`;
    default:
      return null;
  }
}

/**
 * Was der Plan noch nicht fordert — Betrag fuer den Knopf „Restzahlung anlegen“ (13.10.2026).
 * Reines Ablesen des Server-Werts, keine Rechnung. `null`, wenn kein Knopf erscheinen soll:
 * keine Kennzahl, noch kein Plan, Plan nicht zu knapp.
 */
export function fehlbetragCents(deckung: Deckung | null | undefined): number | null {
  // „unterdeckt“ heisst serverseitig bereits: es fehlt ein positiver Betrag. Hier wird nichts verglichen.
  if (!deckung || !deckung.zeilenImPlan || deckung.richtung !== "unterdeckt") return null;
  return deckung.differenzCents;
}
